import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Check, X, Sparkles, CreditCard } from "lucide-react";

const TIER_ORDER = { trial: 0, lite: 1, pro: 2, sponsored: 3 };

export default function Billing() {
  const [plans, setPlans] = useState([]);
  const [currency, setCurrency] = useState("GBP");
  const [sub, setSub] = useState(null);
  const [annual, setAnnual] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [promos, setPromos] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/billing/plans"),
      api.get("/billing/subscription"),
      api.get("/billing/promotions/active").catch(() => ({ data: [] })),
    ])
      .then(([p, s, pr]) => {
        setPlans(p.data.plans); setCurrency(p.data.currency); setSub(s.data);
        const list = pr.data || [];
        setPromos(list);
        const hasAnnual = list.some((x) => !x.plan_lookup_keys.length || x.plan_lookup_keys.some((k) => k.endsWith("annual")));
        if (hasAnnual) setAnnual(true);
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load billing."))
      .finally(() => setLoading(false));
  }, []);

  const checkout = async (lookup_key) => {
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/billing/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Checkout failed.");
      setBusy("");
    }
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't open billing portal.");
      setBusy("");
    }
  };

  if (loading) return <div className="py-24 text-center"><Loader2 className="h-8 w-8 mx-auto animate-spin text-accent" /></div>;

  const fmt = (p) => `£${p.gbp}`;
  const shown = plans.filter((p) => (annual ? p.lookup_key.endsWith("annual") : p.lookup_key.endsWith("monthly")));
  const cards = [
    { tier: "lite", name: "Lite", tagline: "For focused job seekers", perks: ["5 CV tailorings / day", "5 cover letters / day", "1 job profile · 1 base CV", "AI job matching & apply-assist", "Auto-apply assistant", "Application tracker", "CV analysis & role-fit scoring"], excluded: ["LinkedIn profile optimization", "Interview preparation"] },
    { tier: "pro", name: "Pro", tagline: "For serious, multi-track searches", perks: ["10 CV tailorings / day", "10 cover letters / day", "3 job profiles · 3 base CVs", "AI job matching & apply-assist", "Auto-apply assistant", "Application tracker", "CV analysis & role-fit scoring", "LinkedIn profile optimization", "Interview preparation"] },
    { tier: "sponsored", name: "Sponsored Jobs", tagline: "Sponsor-licensed & NHS matches", perks: ["Everything in Pro (incl. LinkedIn optimization & interview prep), plus:", "Up to 12 sponsored roles daily (depending on preferred role and availability)", "1–3 NHS roles / day (role-dependent)", "NHS supporting-information generator (1,400–1,500 words, Word & PDF)", "Auto-enhanced CV per NHS role", "Waterfall matching · never padded"] },
  ];
  const isActivePaid = sub?.status === "active";
  const annualPromo = promos.find((p) => !p.plan_lookup_keys.length || p.plan_lookup_keys.some((k) => k.endsWith("annual")));

  return (
    <div className="max-w-5xl mx-auto space-y-8" data-testid="billing-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Plans & billing</h1>
        <p className="text-muted-foreground mt-1 text-sm">Prices are in GBP (£). UK customers pay in £; everyone else sees their own local currency at checkout, converted automatically and settled in GBP.</p>
      </div>

      {annualPromo && (
        <div className="rounded-xl p-4 bg-gradient-to-r from-[#1a3060] to-[#2c6aa8] text-white flex items-center gap-3 shadow-md" data-testid="promo-banner">
          <Sparkles className="h-5 w-5 text-[#F0B429] shrink-0" strokeWidth={2} />
          <p className="text-sm font-medium">
            <span className="font-bold">Limited time:</span>{" "}
            {annualPromo.discount_type === "percent" ? `${annualPromo.value}% off` : `£${annualPromo.value} off`} annual plans
            {annualPromo.duration === "once" ? " for your first year" : annualPromo.duration === "forever" ? ", every year" : ` for ${annualPromo.duration_in_months} months`}
            {annualPromo.ends_at ? ` · ends ${new Date(annualPromo.ends_at).toLocaleDateString()}` : ""}. Applied automatically at checkout.
          </p>
        </div>
      )}

      <div className={`rounded-xl p-5 border ${sub?.locked ? "bg-red-50 border-red-200" : "bg-white border-zinc-200"}`} data-testid="billing-status">
        {isActivePaid ? (
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm">You're on <strong className="capitalize">{sub.tier}</strong>{sub.lookup_key?.includes("annual") ? " (annual)" : " (monthly)"}. Renews {sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : "soon"}.</p>
            <Button variant="outline" size="sm" onClick={openPortal} disabled={busy === "portal"} data-testid="manage-billing-btn">
              {busy === "portal" ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CreditCard className="h-4 w-4 mr-1.5" />} Manage billing
            </Button>
          </div>
        ) : sub?.locked ? (
          <p className="text-sm text-red-800 font-medium">Your free trial has ended. Upgrade below to keep tailoring CVs, generating cover letters, and matching jobs.</p>
        ) : (
          <p className="text-sm">You're on the <strong>free trial</strong> — {sub?.trial_days_left ?? 0} day{sub?.trial_days_left === 1 ? "" : "s"} left. Today: {sub?.usage_today?.tailor ?? 0}/{sub?.caps?.tailor} tailorings, {sub?.usage_today?.cover ?? 0}/{sub?.caps?.cover} cover letters.</p>
        )}
      </div>

      <div className="flex items-center justify-center gap-3" data-testid="billing-cadence-toggle">
        <span className={`text-sm ${!annual ? "font-semibold" : "text-muted-foreground"}`}>Monthly</span>
        <button onClick={() => setAnnual((a) => !a)} className="relative w-12 h-6 rounded-full bg-zinc-200 transition-colors" data-testid="cadence-switch">
          <span className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-primary transition-transform ${annual ? "translate-x-6" : ""}`} />
        </button>
        <span className={`text-sm ${annual ? "font-semibold" : "text-muted-foreground"}`}>Annual <span className="text-accent">(13–15 month terms)</span></span>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {cards.map((c) => {
          const plan = shown.find((p) => p.tier === c.tier);
          if (!plan) return null;
          const current = isActivePaid && sub.tier === c.tier;
          const highlight = c.tier === "pro" || c.tier === "sponsored";
          return (
            <div key={c.tier} className={`bg-white border rounded-2xl p-6 flex flex-col ${c.tier === "sponsored" ? "border-[#1a3060] shadow-lg" : c.tier === "pro" ? "border-accent shadow-lg" : "border-zinc-200"}`} data-testid={`plan-card-${c.tier}`}>
              {c.tier === "pro" && <span className="self-start text-xs font-semibold uppercase tracking-wider text-accent bg-accent/10 px-2.5 py-1 rounded-full mb-3">Most popular</span>}
              {c.tier === "sponsored" && <span className="self-start text-xs font-semibold uppercase tracking-wider text-[#1a3060] bg-[#1a3060]/10 px-2.5 py-1 rounded-full mb-3">Premium</span>}
              <h3 className="font-heading text-xl font-bold">{c.name}</h3>
              <p className="text-sm text-muted-foreground">{c.tagline}</p>
              <div className="mt-4 mb-1">
                <span className="font-heading text-4xl font-bold">{fmt(plan)}</span>
                <span className="text-muted-foreground text-sm"> / {plan.cadence}</span>
              </div>
              <p className="text-xs text-muted-foreground mb-5">£{plan.gbp} · billed in GBP · local currency shown at checkout</p>
              <ul className="space-y-2.5 mb-6 flex-1">
                {c.perks.map((perk) => (
                  <li key={perk} className="flex items-start gap-2 text-sm"><Check className="h-4 w-4 text-green-600 mt-0.5 shrink-0" strokeWidth={2.5} />{perk}</li>
                ))}
                {(c.excluded || []).map((x) => (
                  <li key={x} className="flex items-start gap-2 text-sm text-muted-foreground"><X className="h-4 w-4 text-zinc-300 mt-0.5 shrink-0" strokeWidth={2.5} /><span className="line-through">{x}</span></li>
                ))}
              </ul>
              <Button
                onClick={() => checkout(plan.lookup_key)}
                disabled={!!busy || current}
                className="w-full h-11 active:scale-[0.98] transition-transform"
                variant={highlight ? "default" : "outline"}
                data-testid={`upgrade-${plan.lookup_key}`}
              >
                {busy === plan.lookup_key ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
                {current ? "Current plan" : `Choose ${c.name}`}
              </Button>
              {c.tier === "sponsored" && (
                <p className="text-[11px] leading-snug text-muted-foreground mt-3" data-testid="sponsored-disclaimer">
                  We aim for up to 12 sponsor-licensed & NHS jobs each day, matched to your chosen roles. Because we only ever show roles at employers with a confirmed UK sponsor licence and never pad your list, some days — especially for niche or specialist roles — you may see fewer. NHS roles depend on live vacancies for your roles. This is a job-matching service, not a job guarantee.
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
